import { ref } from 'vue'
import Api from '@/api'
import { CurrentLiveStream, TG4Stream, TG4StreamState } from '@/types/cms';

type CMSLiveStreamState = 'loading' | 'error' | 'success'

const loggingEnabled = process.env.NODE_ENV !== 'production';
const log = (msg: string) => { if (loggingEnabled) console.log(`[LiveStreams] ${msg}`) }

const pageState = ref<CMSLiveStreamState>('loading')
const pageStreams = ref<CurrentLiveStream[]>([])

let pollTimeout: ReturnType<typeof setTimeout> | null = null
let isPolling = false

async function refreshLiveStreams() {
  const state = pageState
  const streams = pageStreams

  try {
    log('Polling...')
    isPolling = true
    if (state.value !== 'success') {
      state.value = 'loading'
    }
    const response = await Api.CMS.getLiveStreams()

    //Remove offline
    const liveStreams = response.streams.filter(s => s.state != TG4StreamState.DISABLED)

    streams.value = liveStreams
    state.value = 'success'
    log(`Loaded ${liveStreams.length} streams`)

    // Schedule next poll
    if (isPolling && response.pollIntervalSeconds > 0) {
      log(`Next poll in ${response.pollIntervalSeconds}s`)
      if (pollTimeout !== null) {
        clearTimeout(pollTimeout)
      }
      pollTimeout = setTimeout(refreshLiveStreams, response.pollIntervalSeconds * 1000)
    }
  } catch (error) {
    log(`Failed to fetch: ${error}`)
    state.value = 'error'
    // Retry after default interval on error
    if (isPolling) {
      if (pollTimeout !== null) {
        clearTimeout(pollTimeout)
      }
      pollTimeout = setTimeout(refreshLiveStreams, 10 * 1000)
    }
  }
}

function startPolling() {
  if (isPolling) {
    log('Already polling, skipping')
    return
  }
  log(`Polling started`)
  isPolling = true
  refreshLiveStreams()
}

function stopPolling() {
  log('Polling stopped')
  isPolling = false
  if (pollTimeout !== null) {
    clearTimeout(pollTimeout)
    pollTimeout = null
  }
}


async function lazyLoadPageStreams(): Promise<void> {
  if (pageState.value !== 'success') {
    const response = await Api.CMS.getLiveStreams()
    pageStreams.value = response.streams
    pageState.value = 'success'
  }
}

async function getStreamById(videoId: string): Promise<CurrentLiveStream | undefined> {
  await lazyLoadPageStreams()
  return pageStreams.value.find(s => s.streamId === videoId)
}

async function getStreamByChannel(channel: TG4Stream): Promise<CurrentLiveStream | undefined> {
  await lazyLoadPageStreams()
  return pageStreams.value.find(s => s.stream === channel)
}

function isStreamPlayable(stream: CurrentLiveStream): boolean {
  if (!stream.streamId) return false
  if (stream.state === TG4StreamState.UPCOMING || stream.state === TG4StreamState.DISABLED) return false
  if (stream.state === TG4StreamState.OFFAIR && stream.stream !== TG4Stream.CULA4) return false
  return true
}

function playerPath(stream: CurrentLiveStream, deeplink = false): string {
  const isCula4 = stream.stream === TG4Stream.CULA4
  const isOnDemand = stream.stream === TG4Stream.ONDEMAND
  const offset = stream.offsetSeconds || 0
  return `/player/${stream.streamId}?live=true${isCula4 ? '&cula4=true' : ''}${isOnDemand ? '&ondemand=true' : ''}${offset ? `&offset=${-offset}` : ''}${deeplink ? '&deeplink=true' : ''}`
}

export const CMSLiveStreamManager = {
  pageState,
  pageStreams,
  startPolling,
  refreshLiveStreams,
  stopPolling,
  getStreamById,
  getStreamByChannel,
  isStreamPlayable,
  playerPath
}
